import React, { memo, useCallback, useMemo, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";

import DraggableImage from "@/components/dnd/DraggableImage";
import ZoomableImage from "@/components/ZoomableImage";
import RRDDirectMailAssetFilter from "./RRDDirectMailAssetFilter";
import RRDDirectMailToolTips from "../RRDDirectMailToolTips";
import { rrdDirectMailCampaignHelper } from "@/features/campaigns/direct-mail/rrd/utils/rrdDirectMailHelper";
import NoAssetFoundMsg from "./NoAssetFoundMsg";
import {
  getRRDAssetInstruction,
  getRRDAssetPanelSections,
} from "./rrdAssetPanelHelper";
import type { RRDAsset, RRDAssetGroups } from "./rrdTemplateTypes";

function RRDDirectMailAssets({
  heroImages = [],
  assets,
  printType,
  layoutKey,
  isBulk = false,
}: {
  heroImages?: RRDAsset[];
  assets?: RRDAssetGroups;
  printType?: string;
  layoutKey?: string | null;
  isBulk?: boolean;
}) {
  const [selectedLabel, setSelectedLabel] = useState<unknown[]>([]);

  const filteredAssets = useMemo(() => {
    return rrdDirectMailCampaignHelper.getFilteredAssets(assets, selectedLabel);
  }, [assets, selectedLabel]);

  const handleOnSelectLabel = useCallback((v: unknown[]) => {
    setSelectedLabel(v);
  }, []);

  const sections = useMemo(
    () => getRRDAssetPanelSections({ printType, layoutKey }),
    [printType, layoutKey]
  );

  const instruction = useMemo(
    () => getRRDAssetInstruction({ printType, layoutKey, isBulk }),
    [printType, layoutKey, isBulk]
  );

  return (
    <Box
      sx={{
        // overflowX: "hidden",
        // overflowY: "auto",
        // height: 600,
        pr: 1,
      }}
    >
      <RRDDirectMailAssetFilter
        assets={assets}
        onSelectLabel={handleOnSelectLabel}
      />
      <Typography variant="caption" sx={{ my: 1 }}>
        {instruction}
      </Typography>

      {sections.map((section) => (
        <React.Fragment key={section.assetType}>
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", my: 2 }}
            gutterBottom
          >
            {section.label}
            <RRDDirectMailToolTips type={section.tooltipType} />
          </Typography>

          <Grid container spacing={1}>
            {filteredAssets?.[section.key]?.length ? (
              filteredAssets[section.key].map((item) => {
                const isSelectedHero = heroImages?.find(
                  (h) => h?.id === item?.id
                );
                return (
                  <Grid
                    size={{
                      xs: "auto",
                    }}
                    key={item?.id}
                  >
                    <ZoomableImage
                      src={item.compress_url}
                      zoomSrc={item.file_name}
                      alt={item.asset_name}
                      dndId={item.id}
                    >
                      <DraggableImage
                        dndId={item.id}
                        data={{
                          ...item,
                          asset_type: section.assetType,
                          assetType: section.assetType,
                        }}
                        src={item.compress_url}
                        alt={item.asset_name}
                        disabled={
                          section.key !== "coupons" && isSelectedHero
                            ? true
                            : false
                        }
                      />
                    </ZoomableImage>
                  </Grid>
                );
              })
            ) : (
              <NoAssetFoundMsg type={section.emptyType} />
            )}
          </Grid>
        </React.Fragment>
      ))}
    </Box>
  );
}

export default memo(RRDDirectMailAssets);
